import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { activeChips, InboxFilters } from '@/lib/publication-inbox'
import type { TeamMember } from '@/services/team-service'

interface InboxFilterBarProps {
  filters: InboxFilters
  members: TeamMember[]
  onChange: (patch: Partial<InboxFilters>) => void
}

/** Busca, filtros (processo vinculado, responsável, tribunal, período) e chips removíveis. */
export function InboxFilterBar({ filters, members, onChange }: InboxFilterBarProps) {
  const [search, setSearch] = useState(filters.q ?? '')
  const [tribunal, setTribunal] = useState(filters.tribunal ?? '')

  useEffect(() => setSearch(filters.q ?? ''), [filters.q])
  useEffect(() => setTribunal(filters.tribunal ?? ''), [filters.tribunal])

  const nameOf = (id: string) => members.find((m) => m.id === id)?.fullName ?? 'colega'
  const chips = activeChips(filters, { responsavel: nameOf })

  return (
    <div className="flex flex-col gap-2">
      <form
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-6"
        onSubmit={(e) => {
          e.preventDefault()
          onChange({ q: search.trim() || undefined, tribunal: tribunal.trim().toUpperCase() || undefined })
        }}
      >
        <Input
          aria-label="Buscar no texto"
          placeholder="Buscar no texto ou nº do processo"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="lg:col-span-2"
        />
        <NativeSelect
          aria-label="Processo vinculado"
          value={filters.processoVinculado ?? ''}
          onChange={(e) => onChange({ processoVinculado: (e.target.value || undefined) as InboxFilters['processoVinculado'] })}
        >
          <option value="">Processo vinculado: todos</option>
          <option value="sim">Com processo vinculado</option>
          <option value="nao">Sem processo vinculado</option>
        </NativeSelect>
        <NativeSelect
          aria-label="Responsável"
          value={filters.responsavel ?? ''}
          onChange={(e) => onChange({ responsavel: e.target.value || undefined })}
        >
          <option value="">Responsável: todos</option>
          <option value="none">Sem responsável</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>{m.fullName || m.email}</option>
          ))}
        </NativeSelect>
        <Input
          aria-label="Tribunal"
          placeholder="Tribunal (ex.: TJBA)"
          value={tribunal}
          onChange={(e) => setTribunal(e.target.value)}
          onBlur={() => onChange({ tribunal: tribunal.trim().toUpperCase() || undefined })}
        />
        <div className="flex gap-2">
          <Input aria-label="Data inicial" type="date" value={filters.dataInicio ?? ''} onChange={(e) => onChange({ dataInicio: e.target.value || undefined })} />
          <Input aria-label="Data final" type="date" value={filters.dataFim ?? ''} onChange={(e) => onChange({ dataFim: e.target.value || undefined })} />
        </div>
        <button type="submit" className="sr-only">Aplicar filtros</button>
      </form>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <Badge key={chip.key} variant="secondary" className="gap-1">
              {chip.label}
              <button type="button" aria-label={`Remover filtro ${chip.label}`} onClick={() => onChange({ [chip.key]: undefined })}>
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          <Button
            variant="link"
            size="xs"
            onClick={() => onChange({ processoVinculado: undefined, responsavel: undefined, tribunal: undefined, dataInicio: undefined, dataFim: undefined, q: undefined })}
          >
            Limpar filtros
          </Button>
        </div>
      )}
    </div>
  )
}
